import React from "react";
import { lighten, useTheme } from "@mui/material/styles";
import { Box, Chip, IconButton } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

export const Pill = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "left",
    }}
  >
    <Box
      sx={{ fontSize: "0.6rem", color: "text.secondary", fontWeight: "medium" }}
    >
      {label}
    </Box>
    <Chip label={value} size="small" />
  </Box>
);

export const SelectedItemContent = ({
  option,
  config,
  variant,
  filterPillsByShowInTag = true,
  hideSubtitle = false,
}) => {
  const pillsToShow = filterPillsByShowInTag
    ? (config?.pills || []).filter((pill) => pill.showInTag)
    : config?.pills || [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          color: "text.primary",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option[config?.mainTitleKey || "label"]}
      </Box>
      {variant === "subtitle" && !hideSubtitle ? (
        <Box
          sx={{
            color: "text.secondary",
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {option[config?.subtitleKey]}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {pillsToShow
            .filter((pill) => {
              const value = option[pill.key];
              return value !== null && value !== undefined && value !== "";
            })
            .map((pill) => (
              <Pill key={pill.key} label={pill.label} value={option[pill.key]} />
            ))}
        </Box>
      )}
    </Box>
  );
};

export const SelectedItem = ({
  option,
  config,
  variant,
  hasVisiblePills,
  onDelete,
  disabled,
  showClear = true,
  isMulti = false,
  endAdornment = null,
  onClick,
  onKeyDown,
  ...boxProps
}) => {
  const theme = useTheme();
  const isClickable = !!onClick && !disabled;
  const displayLabel = option[config?.mainTitleKey || "label"];

  return (
    <Box
      {...boxProps}
      onClick={onClick}
      onKeyDown={onKeyDown}
      // When the chip acts as a trigger (has onClick), make it keyboard-accessible
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? "button" : undefined}
      aria-label={isClickable ? `${displayLabel}, press Enter to change` : undefined}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingX: "8px",
        paddingY: hasVisiblePills ? "5px" : 0,
        backgroundColor: lighten(theme.palette.divider, 0.5),
        borderRadius: 2,
        marginBottom: isMulti ? "2px" : 0,
        cursor: isClickable ? "pointer" : "default",
        outline: "none",
        "&:focus-visible": isClickable
          ? {
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            }
          : undefined,
      }}
    >
      <SelectedItemContent
        option={option}
        config={config}
        variant={variant}
        hideSubtitle={variant === "subtitle"}
      />
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {showClear && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(e);
            }}
            aria-label={`Clear ${displayLabel}`}
            sx={{ marginRight: "-7px", padding: "4px" }}
            disabled={disabled}
          >
            <CancelIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        )}
        {endAdornment}
      </Box>
    </Box>
  );
};
